/**
 * Lo que el navegador recuerda de los últimos minutos, para poder reportarlo.
 *
 * ⚠ **Sin esto el asistente es una caja de texto.** Hoy, cuando alguien dice «me dio
 * error», no queda rastro de qué error fue: ni el código, ni la ruta, ni si llegó a
 * salir la petición. Reproducir depende de que quien reporta recuerde y sepa
 * describir, y normalmente no es ninguna de las dos cosas.
 *
 * ⚠ **NUNCA se guarda lo que el usuario teclea.** Una miga de escritura registra la
 * ETIQUETA del campo —«escribió en Motivo del egreso»— y jamás su contenido. Una
 * bitácora de teclas es un registrador de pulsaciones, por buenas que sean las
 * intenciones, y aquí pasarían por delante cédulas, montos y claves. El esquema del
 * servidor tampoco admite un campo de valor: son dos cierres, no uno.
 *
 * ⚠ **Vive en memoria y sólo en memoria.** Nada de `localStorage`: la evidencia de
 * una sesión no tiene por qué sobrevivir a cerrar la pestaña, y guardada en disco
 * sería un archivo con rutas y códigos de un cliente esperando a que alguien abra
 * ese navegador.
 *
 * ⚠ **Anillo con tope, no lista.** Ocho horas de trabajo con la pestaña abierta
 * llenarían la memoria del navegador. Se guardan las últimas, que son las cercanas
 * al fallo; las de las nueve de la mañana no sirven para un error de las cinco.
 */

export type Senal = {
  clase: "API" | "EXCEPCION" | "RENDER" | "RED"
  momento: string
  metodo?: string
  ruta?: string
  estado?: number
  codigo?: string
  mensaje?: string
  nombre?: string
  detalles?: Record<string, unknown> | null
}

export type Miga = {
  momento: string
  tipo: "CLIC" | "RUTA" | "ENVIO" | "ESCRITURA"
  /** Lo que el control DICE. Nunca lo que el usuario escribió. */
  etiqueta: string
}

/** El servidor recorta a 25 y 40; aquí se guarda algo más para poder elegir. */
const TOPE_SENALES = 40
const TOPE_MIGAS = 60

const _senales: Senal[] = []
const _migas: Miga[] = []

function ahora(): string {
  return new Date().toISOString()
}

function empujar<T>(lista: T[], entrada: T, tope: number): void {
  lista.push(entrada)
  if (lista.length > tope) lista.splice(0, lista.length - tope)
}

export function registrarSenal(senal: Omit<Senal, "momento">): void {
  empujar(_senales, { ...senal, momento: ahora() }, TOPE_SENALES)
}

export function registrarMiga(tipo: Miga["tipo"], etiqueta: string): void {
  const limpia = etiqueta.trim().replace(/\s+/g, " ").slice(0, 200)
  if (limpia === "") return
  const ultima = _migas[_migas.length - 1]
  // Diez clics seguidos en el mismo botón son una miga, no diez: llenarían el
  // tope y expulsarían lo que pasó antes, que es lo que hace falta.
  if (ultima !== undefined && ultima.tipo === tipo && ultima.etiqueta === limpia) return
  empujar(_migas, { momento: ahora(), tipo, etiqueta: limpia }, TOPE_MIGAS)
}

export function senales(): Senal[] {
  return [..._senales]
}

export function migas(): Miga[] {
  return [..._migas]
}

/** Sólo para las pruebas y para después de enviar un reporte. */
export function limpiar(): void {
  _senales.length = 0
  _migas.length = 0
}

/**
 * El nombre visible de un control, para una miga.
 *
 * ⚠ **Se busca el NOMBRE, y por ese orden.** `aria-label` primero porque es lo que
 * anuncia un lector de pantalla; luego el texto, que es lo que ve todo el mundo; y
 * `name`/`id` al final, que son de programador pero mejor que «(sin nombre)».
 * `value` no aparece en la lista a propósito: es el contenido, no el nombre.
 */
export function nombreDe(elemento: Element): string {
  const etiquetado = elemento.getAttribute("aria-label")
  if (etiquetado !== null && etiquetado.trim() !== "") return etiquetado

  const porId = elemento.getAttribute("aria-labelledby")
  if (porId !== null) {
    const referido = document.getElementById(porId)
    if (referido?.textContent) return referido.textContent
  }

  if (elemento instanceof HTMLInputElement || elemento instanceof HTMLTextAreaElement) {
    const rotulo = elemento.labels?.[0]
    if (rotulo?.textContent) return rotulo.textContent
    // El marcador de posición es texto de la interfaz, no del usuario.
    const marcador = elemento.getAttribute("placeholder")
    if (marcador !== null && marcador.trim() !== "") return marcador
    return elemento.name || elemento.id || "(campo sin nombre)"
  }

  const texto = elemento.textContent
  if (texto !== null && texto.trim() !== "") return texto
  return elemento.getAttribute("data-testid") ?? elemento.tagName.toLowerCase()
}

/**
 * Engancha los oyentes globales. Se llama una vez, al arrancar la aplicación.
 *
 * ⚠ **En captura y pasivos.** En captura para ver el clic aunque algo lo detenga
 * antes; pasivos para no añadir ni un milisegundo a la interacción. Registrar una
 * miga no puede ser la razón por la que un botón va lento.
 */
export function engancharBitacora(ventana: Window = window): () => void {
  const alFallar = (e: ErrorEvent) => {
    registrarSenal({
      clase: "EXCEPCION",
      nombre: e.error instanceof Error ? e.error.name : "Error",
      mensaje: e.message,
    })
  }

  const alRechazar = (e: PromiseRejectionEvent) => {
    const causa = e.reason
    registrarSenal({
      clase: "EXCEPCION",
      nombre: causa instanceof Error ? causa.name : "Rechazo sin capturar",
      mensaje: causa instanceof Error ? causa.message : String(causa),
    })
  }

  const alPulsar = (e: Event) => {
    const objetivo = e.target
    if (!(objetivo instanceof Element)) return
    const control = objetivo.closest("button, a, [role='button'], [role='tab'], summary")
    if (control === null) return
    registrarMiga("CLIC", nombreDe(control))
  }

  const alEscribir = (e: Event) => {
    const objetivo = e.target
    if (
      objetivo instanceof HTMLInputElement ||
      objetivo instanceof HTMLTextAreaElement ||
      objetivo instanceof HTMLSelectElement
    ) {
      // ⚠ Sólo el nombre del campo. `objetivo.value` no se lee aquí ni se leerá.
      registrarMiga("ESCRITURA", nombreDe(objetivo))
    }
  }

  const alEnviar = (e: Event) => {
    const objetivo = e.target
    if (objetivo instanceof HTMLFormElement) {
      registrarMiga("ENVIO", objetivo.getAttribute("aria-label") ?? "(formulario)")
    }
  }

  ventana.addEventListener("error", alFallar)
  ventana.addEventListener("unhandledrejection", alRechazar)
  ventana.document.addEventListener("click", alPulsar, { capture: true, passive: true })
  ventana.document.addEventListener("change", alEscribir, { capture: true, passive: true })
  ventana.document.addEventListener("submit", alEnviar, { capture: true, passive: true })

  return () => {
    ventana.removeEventListener("error", alFallar)
    ventana.removeEventListener("unhandledrejection", alRechazar)
    ventana.document.removeEventListener("click", alPulsar, { capture: true })
    ventana.document.removeEventListener("change", alEscribir, { capture: true })
    ventana.document.removeEventListener("submit", alEnviar, { capture: true })
  }
}
